import { JSX, ReactNode } from "react";
import { Radio } from "./components/Radio";

import { Text, Color, Flex, InfoTooltip } from "src/elements";

interface Props<T> {
  options: { label: string; value: T; isDisabled?: boolean }[];
  value: T;
  setValue: (value: T) => void;
  label?: string;
  tooltip?: ReactNode;
  width?: string;
  direction?: "column" | "row";
  size?: "default" | "small";
  isUnselectable?: boolean;
  variant?: "body2" | "small2";
  isDisabled?: boolean;
  optionsCustomRender?: () => JSX.Element | null;
}

export const Radios = <T,>({
  options,
  value,
  setValue,
  label,
  tooltip,
  width = "100%",
  direction = "row",
  size = "default",
  isUnselectable = false,
  isDisabled = false,
  variant,
  optionsCustomRender,
}: Props<T>) => (
  <Flex width={width} justify="between" align="center" gap="10px" direction={direction}>
    {label && (
      <Flex>
        <Text
          variant={size === "small" ? "caption1" : "body1"}
          color={
            (isDisabled && Color.textDisabled) ||
            (size === "small" && Color.primaryActive) ||
            Color.textMain
          }
        >
          {label}
        </Text>

        {tooltip && <InfoTooltip tooltip={tooltip} />}
      </Flex>
    )}

    <Flex gap="5px 10px" direction={direction}>
      {options.map((option, i) => (
        <Radio
          key={i}
          label={option.label}
          value={option.value}
          setIsChecked={setValue}
          size={size}
          variant={variant}
          isChecked={option.value === value}
          isDisabled={isDisabled || option.isDisabled}
          isUnselectable={isUnselectable}
        />
      ))}

      {optionsCustomRender && options.find((o) => o.value === value) && (
        <Flex margin="10px" justify="center">
          {optionsCustomRender()}
        </Flex>
      )}
    </Flex>
  </Flex>
);
